import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Network, ConnectionStatus } from '@capacitor/network';

/**
 * Service to monitor network connectivity and state changes
 * Helps improve token refresh reliability during network switches (WiFi ↔ Cellular)
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkMonitorService {
  private isNetworkSwitching = false;
  private lastNetworkType: string | null = null;
  private switchingTimeout: any = null;

  constructor() {
    this.initializeNetworkMonitoring();
  }

  /**
   * Initialize network state change listener (mobile only)
   * Detects when user switches between WiFi and cellular
   */
  private async initializeNetworkMonitoring() {
    if (!Capacitor.isNativePlatform()) {
      return; // Web doesn't need this - network switches are handled by browser
    }

    try {
      // Get initial network state
      const status = await Network.getStatus();
      this.lastNetworkType = status.connectionType;

      // Listen for network changes
      Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        const newNetworkType = status.connectionType;
        const wasConnected = this.lastNetworkType !== 'none';
        const isConnected = status.connected;

        // Detect network switch (WiFi ↔ Cellular)
        if (wasConnected && isConnected && this.lastNetworkType !== newNetworkType) {
          console.warn(`[NetworkMonitor] Network switch detected: ${this.lastNetworkType} → ${newNetworkType}`);
          this.markNetworkSwitching();
        }
        // Detect reconnection after being offline
        else if (!wasConnected && isConnected) {
          console.warn(`[NetworkMonitor] Network reconnected: ${newNetworkType}`);
          this.markNetworkSwitching();
        }
        // Detect disconnection
        else if (wasConnected && !isConnected) {
          console.warn('[NetworkMonitor] Network disconnected');
          this.isNetworkSwitching = false;
        }

        this.lastNetworkType = newNetworkType;
      });

      console.warn('[NetworkMonitor] Initialized with connection type:', this.lastNetworkType);
    } catch (error) {
      console.warn('[NetworkMonitor] Failed to initialize network monitoring:', error);
    }
  }

  /**
   * Mark that network is currently switching
   * Automatically clears after 3 seconds
   */
  private markNetworkSwitching() {
    this.isNetworkSwitching = true;

    // Clear any existing timeout
    if (this.switchingTimeout) {
      clearTimeout(this.switchingTimeout);
    }

    // After 3 seconds, assume network switch is complete
    this.switchingTimeout = setTimeout(() => {
      this.isNetworkSwitching = false;
      console.warn('[NetworkMonitor] Network switch stabilization complete');
    }, 3000);
  }

  /**
   * Check if network is currently available
   * Returns true if connected, false if offline
   */
  async isNetworkAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      // Web: use navigator.onLine first (fast check)
      if (!navigator.onLine) {
        return false;
      }

      // Additional check: ping a lightweight endpoint with 5s timeout
      // This detects DNS failures, firewall blocks, and server unreachability.
      // 5s (not 2s) so a slow cold-start backend isn't misread as "offline" — a
      // false negative here skips token refresh and cascades into 401s on first load.
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // Use GET (not HEAD): the health action is [HttpGet]-only, and ASP.NET Core
        // attribute routing returns 404 for a HEAD request to a GET-only route — which
        // would make this probe always report "offline". The payload is tiny.
        const response = await fetch('/api/health', {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-cache'
        });

        clearTimeout(timeoutId);
        return response.ok;
      } catch (error) {
        // Network ping failed - likely DNS issue, firewall, or server down
        console.warn('[NetworkMonitor] Network health check failed:', error);
        return false;
      }
    }

    try {
      // Mobile: use Capacitor Network plugin
      const status = await Network.getStatus();
      return status.connected;
    } catch (error) {
      console.warn('[NetworkMonitor] Failed to check network status, assuming available:', error);
      // Assume available if check fails (better to attempt and fail than block user)
      return true;
    }
  }

  /**
   * Check if network is currently switching (WiFi ↔ Cellular)
   * Returns true during the ~3 second stabilization period after a switch
   */
  isCurrentlySwitching(): boolean {
    return this.isNetworkSwitching;
  }

  /**
   * Get recommended retry configuration based on network state
   * Returns longer delays and more retries when network is switching
   */
  getRetryConfig(): { maxRetries: number; initialDelay: number } {
    if (this.isNetworkSwitching) {
      // Network is switching - use longer delays to allow stabilization
      return {
        maxRetries: 6,      // More retries (WiFi → Cellular can take 5-10s)
        initialDelay: 1000  // Start with 1s instead of 500ms
      };
    }

    // Normal retry config
    return {
      maxRetries: 4,      // Standard retries
      initialDelay: 500   // Standard delay
    };
  }

  /**
   * Get current network type (wifi, cellular, none, unknown)
   * Returns null on web platform
   */
  async getNetworkType(): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      const status = await Network.getStatus();
      return status.connectionType;
    } catch (error) {
      console.warn('[NetworkMonitor] Failed to get network type:', error);
      return null;
    }
  }
}
